import useEditReturnRequestCustomer from "@babackapp/private-api-client/client/use-edit-return-request-customer";
import { useUpdateReturnRequestInCache } from "@babackapp/private-api-client/client/use-invalidate-return-request";
import type { ReturnRequestDto } from "@babackapp/private-api-client/types";
import FormInput from "@babackapp/react-hook-forms-components/form-input";
import useTranslation from "@babackapp/translations/use-translation";
import { Button, ButtonLoading } from "@babackapp/ui/components/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@babackapp/ui/components/dialog";
import { Form, FormField } from "@babackapp/ui/components/form";
import { Pencil } from "lucide-react";
import { useState } from "react";
import useEditCustomerForm from "./use-edit-customer-form";

interface EditCustomerDialogProps {
  returnRequest: ReturnRequestDto;
}

export default function EditCustomerDialog({
  returnRequest,
}: EditCustomerDialogProps) {
  const t = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const updateReturnRequestInCache = useUpdateReturnRequestInCache();
  const { mutate, isPending } = useEditReturnRequestCustomer({
    onSuccess(returnRequest) {
      updateReturnRequestInCache({ returnRequest });
      setIsOpen(false);
    },
  });
  const form = useEditCustomerForm(returnRequest);

  const onSubmit = form.handleSubmit((data) => {
    mutate({
      params: {
        path: {
          returnRequestId: returnRequest.id,
          shopId: returnRequest.shopId,
        },
      },
      body: {
        email: data.email,
        first_name: data.firstName,
        last_name: data.lastName,
        phone: data.phone,
      },
    });
  });

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button size={"icon"} variant={"ghost"}>
          <Pencil className={"h-4 w-4"} />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("returnDetail.rightBar.updateCustomer.title")}
          </DialogTitle>
          <DialogDescription>
            {t("returnDetail.rightBar.updateCustomer.description")}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={onSubmit}>
            <div className="flex gap-4">
              <FormField
                render={({ field }) => (
                  <FormInput label={t("misc.firstName")} {...field} />
                )}
                name={"firstName"}
              />
              <FormField
                render={({ field }) => (
                  <FormInput label={t("misc.name")} {...field} />
                )}
                name={"lastName"}
              />
            </div>
            <FormField
              render={({ field }) => (
                <FormInput
                  label={t("returnRequest.customer.email")}
                  type={"email"}
                  {...field}
                />
              )}
              name={"email"}
            />
            <FormField
              render={({ field }) => (
                <FormInput label={t("address.phone")} {...field} />
              )}
              name={"phone"}
            />
            <DialogFooter className={"mt-6"}>
              <DialogClose asChild>
                <Button variant={"secondary"}>{t("misc.cancel")}</Button>
              </DialogClose>
              <ButtonLoading
                isLoading={isPending}
                disabled={!form.formState.isValid}
                type={"submit"}
              >
                {t("misc.save")}
              </ButtonLoading>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
